import { List, Stack, Text } from '@mantine/core';

import type {
  RegistryDigestCorporate,
  RegistryDigestDocumentSection,
  RegistryDigestFinancials,
  RegistryDocumentDigest,
} from '@/types/registry-documents';
import {
  DOCUMENT_WORDS,
  documentHasContent,
  documentIssueLine,
  documentSectionTitle,
  keyFactRows,
} from './digestDocuments';
import {
  financialDetailLines,
  financialLeadIn,
  financialTable,
  financialUnitLine,
} from './digestFinancials';
import { GridTable, LabelValueTable } from './DigestTables';
import {
  entityNameOf,
  formerDirectorNames,
  MANAGEMENT_COLUMNS,
  managementTableRows,
  OWNERSHIP_COLUMNS,
  ownershipTableRows,
  registrationRows,
  sourceLine,
  TEMPLATE_WORDS,
} from './digestTemplate';

/**
 * Corporate Information's three blocks, under the section's heading, as
 * the research team's template has them: Registration Details as a
 * label/value table with the labels shaded, Management as a table with
 * the former directors listed under it, Ownership / Shareholders
 * numbered. Every registration row is present whatever the documents
 * gave: an empty value reads "Not provided", as the template's comment
 * asks, so a reader sees what was looked for. An empty shareholder cell
 * reads "not indicated". Takes one document's tables or the order-wide
 * ones, which have the same shape.
 */
export function CorporateInformation({
  corporate,
  registry,
  companyName,
}: Readonly<{ corporate: RegistryDigestCorporate; registry: string; companyName: string }>) {
  const entity = entityNameOf(corporate, companyName);
  const former = formerDirectorNames(corporate);
  return (
    <>
      <Text size="sm" fw={600}>
        {TEMPLATE_WORDS.registrationHeading}
      </Text>
      <Text size="sm">{TEMPLATE_WORDS.registrationLeadIn(registry)}</Text>
      <LabelValueTable
        rows={registrationRows(corporate.registration)}
        label={TEMPLATE_WORDS.registrationHeading}
      />

      <Text size="sm" fw={600}>
        {TEMPLATE_WORDS.managementHeading}
      </Text>
      {corporate.management.length > 0 ? (
        <>
          <Text size="sm">{TEMPLATE_WORDS.managementLeadIn(entity)}</Text>
          <GridTable
            columns={MANAGEMENT_COLUMNS}
            rows={managementTableRows(corporate.management)}
            label={TEMPLATE_WORDS.managementHeading}
          />
        </>
      ) : (
        <Text size="sm">{TEMPLATE_WORDS.noManagement(entity)}</Text>
      )}
      {former.length > 0 && (
        <Stack gap={4} data-testid="registry-digest-former">
          <Text size="sm">{TEMPLATE_WORDS.formerLeadIn(registry, entity)}</Text>
          <Text size="sm" fw={500}>
            {former.join(', ')}
          </Text>
          <Text size="sm">{TEMPLATE_WORDS.formerClose}</Text>
        </Stack>
      )}

      <Text size="sm" fw={600}>
        {TEMPLATE_WORDS.ownershipHeading}
      </Text>
      {corporate.shareholders.length > 0 ? (
        <>
          <Text size="sm">{TEMPLATE_WORDS.ownershipLeadIn}</Text>
          {/* Numbered in the digest's order: largest holdings first. */}
          <GridTable
            columns={OWNERSHIP_COLUMNS}
            rows={ownershipTableRows(corporate.shareholders)}
            label={TEMPLATE_WORDS.ownershipHeading}
          />
        </>
      ) : (
        <Text size="sm">{TEMPLATE_WORDS.noShareholders}</Text>
      )}
    </>
  );
}

/**
 * Financial Information from the filed accounts, laid out as the Word
 * file has it: the sentence naming the accounts and the period ends, the
 * table of the figures some period gives, the currency line under it,
 * then the auditor, the opinion and any going-concern statement. What the
 * accounts themselves qualify is with every other document's caveats,
 * after the sections.
 */
export function FinancialInformation({
  financials,
  registry,
}: Readonly<{ financials: RegistryDigestFinancials; registry: string }>) {
  const table = financialTable(financials);
  const unitLine = financialUnitLine(financials);
  const details = financialDetailLines(financials);
  return (
    <Stack gap="xs" data-testid="registry-digest-financials">
      <Text size="sm">{financialLeadIn(financials, registry)}</Text>
      {table && (
        <GridTable
          columns={table.columns}
          rows={table.rows}
          label={financials.title}
          rowHeaders
          numeric
        />
      )}
      {unitLine && <Text size="xs">{unitLine}</Text>}
      {details.map((line) => (
        <Text key={line} size="sm">
          {line}
        </Text>
      ))}
    </Stack>
  );
}

/**
 * A section of its own for a document that is neither corporate nor
 * financial: the line naming the document with its issue date and
 * issuer, the key facts as a label/value table, then the summary. Its
 * caveats are with every other document's, after the sections.
 */
export function DocumentInformation({
  section,
}: Readonly<{ section: RegistryDigestDocumentSection }>) {
  const issue = documentIssueLine(section);
  const facts = keyFactRows(section);
  const summary = section.summary?.trim();
  return (
    <Stack gap="xs" data-testid="registry-digest-document">
      {issue && <Text size="sm">{issue}</Text>}
      {facts.length > 0 && (
        <LabelValueTable rows={facts} label={`${documentSectionTitle(section)}: key facts`} />
      )}
      {summary && <Text size="sm">{summary}</Text>}
      {!documentHasContent(section) && <Text size="sm">{DOCUMENT_WORDS.noInformation}</Text>}
    </Stack>
  );
}

/**
 * What closes the order-wide digest: the documents' own caveats (every
 * section's, gathered by the API) and the sources appendix, each source
 * on a line with the order's delivery date where it gives none.
 */
export function DigestAppendix({
  digest,
  deliveredAt,
}: Readonly<{
  digest: Pick<RegistryDocumentDigest, 'caveats' | 'sources'>;
  deliveredAt: string | null;
}>) {
  return (
    <>
      {digest.caveats.length > 0 && (
        <List size="xs" spacing={2} data-testid="registry-digest-caveats">
          {digest.caveats.map((caveat) => (
            <List.Item key={caveat}>{caveat}</List.Item>
          ))}
        </List>
      )}

      <Text size="sm" fw={600}>
        {TEMPLATE_WORDS.sourcesHeading}
      </Text>
      <Text size="sm">{TEMPLATE_WORDS.sourcesLeadIn}</Text>
      {digest.sources.length > 0 && (
        <List size="sm" spacing={2} data-testid="registry-digest-sources">
          {digest.sources.map((source, index) => (
            // Sources carry no id; the list is as the digest ordered it.
            <List.Item key={`${source.registry ?? ''}:${source.documentTitle ?? ''}:${index}`}>
              {sourceLine(source, deliveredAt)}
            </List.Item>
          ))}
        </List>
      )}
    </>
  );
}
